import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, BarChart3, Sparkles, Loader2, FileText } from "lucide-react";
import { adminMe, adminLogout, adminFetch } from "@/lib/admin-api";

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("leads");
  const navigate = useNavigate();

  useEffect(() => {
    adminMe().then(setMe).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const onLogout = async () => {
    await adminLogout();
    navigate("/admin/login");
  };

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-50">
        <Loader2 className="h-6 w-6 animate-spin text-navy-500" />
      </div>
    );
  }

  const tabs = [
    { k: "leads", label: "Leads", I: Users },
    { k: "attribution", label: "Attribution", I: BarChart3 },
    { k: "blog", label: "Blog Generator", I: Sparkles },
  ];

  return (
    <div className="min-h-screen bg-navy-50/40" data-testid="page-admin">
      <header className="bg-navy-900 text-white">
        <div className="container-x py-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-gold-300">Admin</div>
            <h1 className="font-serif text-2xl">Elite Home Care — Dashboard</h1>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline text-white/70">{me.email}</span>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-4 py-2 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-10">
        <div className="flex gap-2 border-b border-navy-900/10 overflow-x-auto">
          {tabs.map(({ k, label, I }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`inline-flex items-center gap-2 px-5 py-3 border-b-2 transition-colors whitespace-nowrap ${
                tab === k
                  ? "border-gold-500 text-navy-900"
                  : "border-transparent text-navy-500 hover:text-navy-900"
              }`}
            >
              <I className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "leads" && <LeadsTab />}
          {tab === "attribution" && <AttributionTab />}
          {tab === "blog" && <BlogTab />}
        </div>
      </div>
    </div>
  );
}

function LeadsTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    adminFetch("get", "/admin/leads").then(setRows).catch(() => setRows([]));
  }, []);
  if (rows === null) return <Loader />;
  if (!rows.length)
    return <Empty icon={<Users className="h-6 w-6" />} text="No leads yet." />;
  return (
    <div className="bg-white rounded-2xl border border-navy-900/10 overflow-hidden">
      <table className="w-full text-sm" data-testid="admin-leads-table">
        <thead className="bg-navy-50/80 text-navy-500 uppercase text-xs tracking-wider">
          <tr>
            {["When", "Name", "Phone", "Email", "Service", "Message"].map((h) => (
              <th key={h} className="text-left px-5 py-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-navy-900/5">
              <td className="px-5 py-3 text-navy-500 whitespace-nowrap">
                {r.created_at ? new Date(r.created_at).toLocaleString() : "-"}
              </td>
              <td className="px-5 py-3 font-medium text-navy-900">{r.name}</td>
              <td className="px-5 py-3"><a href={`tel:${r.phone}`} className="hover:text-gold-600">{r.phone}</a></td>
              <td className="px-5 py-3"><a href={`mailto:${r.email}`} className="hover:text-gold-600">{r.email}</a></td>
              <td className="px-5 py-3 text-navy-700">{r.service_interest || "-"}</td>
              <td className="px-5 py-3 text-navy-500 max-w-xs truncate">{r.message || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AttributionTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    adminFetch("get", "/admin/attribution/summary").then(setRows).catch(() => setRows([]));
  }, []);
  if (rows === null) return <Loader />;
  if (!rows.length) return <Empty icon={<BarChart3 className="h-6 w-6" />} text="No attribution events yet." />;
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div className="bg-white rounded-2xl border border-navy-900/10 p-6" data-testid="admin-attribution">
      <h2 className="font-serif text-xl text-navy-900">Attribution — last 30 days</h2>
      <div className="mt-6 space-y-3">
        {rows.map((r) => (
          <div key={r.source} className="flex items-center gap-4">
            <div className="w-56 text-sm text-navy-700 truncate">{r.source}</div>
            <div className="flex-1 h-6 bg-navy-50 rounded-full overflow-hidden">
              <div className="h-full bg-gold-400" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
            <div className="w-12 text-right text-sm font-medium text-navy-900">{r.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogTab() {
  const [topic, setTopic] = useState("");
  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [drafts, setDrafts] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  const loadDrafts = () =>
    adminFetch("get", "/admin/blog/drafts").then(setDrafts).catch(() => setDrafts([]));
  useEffect(() => { loadDrafts(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await adminFetch("post", "/admin/blog/generate", {
        topic, primary_keyword: keyword, city_focus: city,
      });
      setTopic(""); setKeyword(""); setCity("");
      await loadDrafts();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Generation failed. Check Universal Key balance.");
    } finally { setBusy(false); }
  };

  const publish = async (id) => {
    await adminFetch("post", "/admin/blog/publish", { id });
    await loadDrafts();
  };

  const preview = drafts?.find((d) => d.id === previewId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8" data-testid="admin-blog">
      <form onSubmit={generate} className="bg-white rounded-2xl border border-navy-900/10 p-6 h-fit">
        <h2 className="font-serif text-xl text-navy-900">Generate a draft</h2>
        <p className="text-sm text-navy-500 mt-1">
          Claude Haiku 4.5 writes a 1500-word SEO-optimized draft. You review before publishing.
        </p>
        <div className="mt-5 space-y-4">
          <Field label="Topic">
            <input value={topic} onChange={(e) => setTopic(e.target.value)} required
              placeholder="E.g. How to prevent falls at home in Naples"
              data-testid="blog-topic"
              className="form-input" />
          </Field>
          <Field label="Primary keyword">
            <input value={keyword} onChange={(e) => setKeyword(e.target.value)} required
              placeholder="fall prevention naples fl"
              data-testid="blog-keyword"
              className="form-input" />
          </Field>
          <Field label="City focus (optional)">
            <input value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="Naples"
              data-testid="blog-city"
              className="form-input" />
          </Field>
          {err && <div className="rounded bg-red-50 border border-red-200 text-red-700 text-sm p-3">{err}</div>}
          <button type="submit" disabled={busy} data-testid="blog-generate" className="btn-primary w-full justify-center">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate draft (≈ 20s)"}
          </button>
        </div>
        <style>{`.form-input{width:100%;border:1px solid rgb(226 232 240);border-radius:12px;padding:10px 14px;color:#0A192F;background:#fff;font-size:14px;outline:none}.form-input:focus{border-color:#D4AF37;box-shadow:0 0 0 4px rgba(212,175,55,0.15)}`}</style>
      </form>

      <div className="bg-white rounded-2xl border border-navy-900/10 p-6">
        <h2 className="font-serif text-xl text-navy-900">Drafts</h2>
        {drafts === null ? <Loader /> : drafts.length === 0 ? (
          <Empty icon={<FileText className="h-6 w-6" />} text="No drafts yet — generate your first above." />
        ) : (
          <div className="mt-4 divide-y divide-navy-900/5">
            {drafts.map((d) => (
              <div key={d.id} className="py-4 flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-navy-900">{d.title}</div>
                  <div className="text-xs text-navy-500 mt-1">
                    <code className="bg-navy-50 px-1.5 py-0.5 rounded">/blog/{d.slug}</code>
                    &nbsp;·&nbsp; {d.status}
                    &nbsp;·&nbsp; {d.created_at ? new Date(d.created_at).toLocaleString() : ""}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setPreviewId(d.id)} data-testid={`blog-preview-${d.slug}`} className="text-sm px-3 py-1.5 rounded-full border border-navy-900/15 hover:border-gold-400 hover:text-gold-600">Preview</button>
                  {d.status !== "published" && (
                    <button onClick={() => publish(d.id)} data-testid={`blog-publish-${d.slug}`} className="text-sm px-3 py-1.5 rounded-full bg-gold-400 text-navy-900 hover:bg-gold-500">Publish</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {preview && (
          <div className="mt-6 pt-6 border-t border-navy-900/10">
            <h3 className="font-serif text-lg text-navy-900">{preview.title}</h3>
            <p className="text-sm text-navy-500 mt-1">{preview.excerpt}</p>
            {(preview.sections || []).slice(0, 2).map((s) => (
              <div key={s.h2} className="mt-4">
                <div className="font-medium text-navy-900">{s.h2}</div>
                <p className="text-sm text-navy-600 mt-1 line-clamp-4">{s.body}</p>
              </div>
            ))}
            <div className="mt-3 text-xs text-navy-500">Full preview will render after publish on /blog/{preview.slug} (requires wiring, see PRD).</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="flex flex-col gap-1.5"><span className="text-xs uppercase tracking-wide text-navy-500">{label}</span>{children}</label>;
}
function Loader() { return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy-500" /></div>; }
function Empty({ icon, text }) {
  return (
    <div className="py-14 text-center">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-navy-50 text-navy-400">{icon}</span>
      <p className="mt-3 text-navy-500">{text}</p>
    </div>
  );
}
